/**
 * Team Upload Hook
 * Wrapper around core upload hook for team shared datadrive
 *
 * BREAKING CHANGE: Now accepts userRole and teamId as hook parameters
 * instead of uploadFile parameters for cleaner API and better performance
 */

import { useCallback, useMemo } from 'react'

import { TeamRole } from '@/consts/team'
import { TeamFSContext } from '@/pages/data-mgr/core/context'
import {
  FILE_STATUS,
  type IUploadTask,
  UPLOAD_RESPONSE_CODE,
  useUploadCore,
} from '@/pages/data-mgr/core/hooks/useUpload'

// Re-export types and enums for backward compatibility
export { FILE_STATUS, UPLOAD_RESPONSE_CODE }
export type { IUploadTask as ITask }

// Deprecated: TeamUploadParams is no longer used in uploadFile signature
// Kept for backward compatibility in type exports
export interface TeamUploadParams {
  userRole: TeamRole
  teamId: number
}

/**
 * Team upload hook
 * @param userRole - User's role in the team (fixed for hook lifecycle)
 * @param teamId - Team ID (fixed for hook lifecycle)
 * @returns [uploadList, uploadFile, successNum]
 *
 * @example
 * const [uploadList, uploadFile, successNum] = useTeamUpload(userRole, teamId)
 * await uploadFile(file, target, domain, { userRole, teamId }) // Old API (still works)
 * await uploadFile(file, target, domain) // New simplified API
 */
export function useTeamUpload(userRole?: TeamRole, teamId?: number) {
  // Create context based on provided role and teamId
  const context = useMemo(
    () => new TeamFSContext(userRole || TeamRole.Normal, teamId || 0),
    [userRole, teamId],
  )

  const {
    uploadList,
    uploadFile: coreUploadFile,
    successNum,
    pauseTask,
    resumeTask,
  } = useUploadCore(context)

  // Wrap uploadFile to maintain backward compatibility with old API
  // that passes params as 4th argument
  const uploadFile = useCallback(
    async (
      file: File,
      target: string,
      domain: number,

      _params?: TeamUploadParams, // Ignored - kept for backward compat
    ) => {
      return coreUploadFile(file, target, domain)
    },
    [coreUploadFile],
  )

  return { uploadList, uploadFile, successNum, pauseTask, resumeTask }
}
